import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  ViewChild,
} from '@angular/core';

import {
  ApexAxisChartSeries,
  ApexDataLabels,
  ApexPlotOptions,
  ApexStates,
  ApexStroke,
  ApexXAxis,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { ApexColorFunction } from '../../molecules/consumption-chart/types/apex-color-function';
import { ConsumptionChartOptions } from '../../molecules/consumption-chart/types/consumption-chart-options';
import {
  ConsumptionSeriesIndex,
  IConsumptionItem,
  ImmomioIconName,
  NgChanges,
} from '@resident-nx/shared';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ApexDataPointSelection } from '../../molecules/consumption-chart/types/apex-data-point-selection';
import { IconWebComponent } from '@resident-nx/web/ui';
import { ConsumptionChartYearA11yDirective } from './consumption-chart-year-a11y.directive';
import { debounceTime, Subject } from 'rxjs';

enum SeriesPeriod {
  FULL_YEAR = 'FULL_YEAR',
  FIRST_SEMESTER = 'FIRST_SEMESTER',
  SECOND_SEMESTER = 'SECOND_SEMESTER',
}

interface EstimatedDataPointIndices {
  [seriesIndex: number]: {
    [dataPointIndex: number]: boolean;
  };
}

@Component({
  standalone: true,
  selector: 'rs-web-consumption-chart-year',
  templateUrl: './consumption-chart-year.component.html',
  styleUrls: ['./consumption-chart-year.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgApexchartsModule, IconWebComponent, ConsumptionChartYearA11yDirective],
})
export class ConsumptionChartYearWebComponent implements OnChanges {
  #breakpointObserver = inject(BreakpointObserver);
  #cdr = inject(ChangeDetectorRef);
  @Input() consumption: IConsumptionItem[];
  @Input() pastConsumption: IConsumptionItem[];

  @Input() selectedDataPointIndex: number;
  @Input() options: ConsumptionChartOptions;

  @Input() small = false;

  @Output() consumptionSelected = new EventEmitter<IConsumptionItem>();

  public readonly SeriesPeriod = SeriesPeriod;
  public readonly ImmomioIconName = ImmomioIconName;

  // holds series for full year, 1st semester, 2nd semester
  public availableSeries: Record<SeriesPeriod, ApexAxisChartSeries>;
  public availableCategories: Record<SeriesPeriod, string[]>;
  // rendered series will be determined by activeSeries
  public activeSeriesPeriod = SeriesPeriod.FULL_YEAR;
  // true only shows first/last 6 months, determined by activeSeriesPeriod
  public isSemesterMode = false;

  public stroke: ApexStroke;
  public plotOptions: ApexPlotOptions;
  public colors: (ApexColorFunction | string)[];
  public states: ApexStates;
  public dataLabels: ApexDataLabels;

  private estimatedDataPointIndices: EstimatedDataPointIndices = {
    [ConsumptionSeriesIndex.PAST_CONSUMPTION]: {},
    [ConsumptionSeriesIndex.CONSUMPTION]: {},
  };
  // this is needed as we need a full array (12 months) for the chart
  // if values are missing for specific months it will get filled with a null value
  // but the original consumption response do not have these null values.
  public consumptionWithFittingDatapointIndex = signal<IConsumptionItem[]>([]);

  #lastChartRerender = new Subject();
  public chartRerendered = toSignal(this.#lastChartRerender.pipe(debounceTime(300)));

  @ViewChild(ChartComponent) chart: ChartComponent;

  constructor() {
    this.initChart();

    this.#breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntilDestroyed())
      .subscribe(state => this.reactOnBreakpointStateChange(state));
  }

  ngOnChanges(changes: NgChanges<ConsumptionChartYearWebComponent>): void {
    if (changes.selectedDataPointIndex) {
      // re-execute bar color functions
      void this.chart?.updateOptions({ colors: this.colors });
    }

    if (changes.options) {
      this.updateXAxis();
      this.registerDataPointSelectionEventHandler();
      this.registerUpdateEventHandler();
    }

    if (changes.consumption) {
      this.updateSeries(ConsumptionSeriesIndex.CONSUMPTION);

      if (this.consumption?.length > 0) {
        const lastIndex = this.consumptionWithFittingDatapointIndex().findLastIndex(
          entry => entry !== null
        );
        this.selectedDataPointIndex = lastIndex;
        const entry = this.consumptionWithFittingDatapointIndex()[lastIndex];
        this.consumptionSelected.emit(entry);
      }
    }

    if (changes.pastConsumption) {
      this.updateSeries(ConsumptionSeriesIndex.PAST_CONSUMPTION);
    }

    if (changes.small) {
      this.plotOptions = this.createPlotOptions();
    }
  }

  private initChart(): void {
    this.availableSeries = {
      [SeriesPeriod.FULL_YEAR]: [
        { name: 'Previous', data: [] },
        { name: 'Selected', data: [] },
      ],
      [SeriesPeriod.FIRST_SEMESTER]: [
        { name: 'Previous', data: [] },
        { name: 'Selected', data: [] },
      ],
      [SeriesPeriod.SECOND_SEMESTER]: [
        { name: 'Previous', data: [] },
        { name: 'Selected', data: [] },
      ],
    };

    const categoryForEachMonth = this.createMonthNames();

    this.availableCategories = {
      [SeriesPeriod.FULL_YEAR]: categoryForEachMonth,
      [SeriesPeriod.FIRST_SEMESTER]: categoryForEachMonth.slice(0, 6),
      [SeriesPeriod.SECOND_SEMESTER]: categoryForEachMonth.slice(6, 12),
    };

    this.states = {
      hover: {
        filter: { type: 'none' },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: { type: 'none' },
      },
    };
    this.colors = [
      // past consumption
      'var(--surface-hover, #F2F2F2)',
      // current consumption
      ({ dataPointIndex, seriesIndex }) => {
        const activeColor = 'var(--consumption-year-active-bar, #3486EF)';
        const inactiveColor = 'var(--consumption-year-inactive-bar, #EBF2FF)';
        const activeColorEstimated = 'var(--Status-Moderate-Default, #F7C976)';
        const inactiveColorEstimated = 'var(--Status-Moderate-Light, #FEF6E9)';
        const isBarSelected = dataPointIndex === this.selectedDataPointIndex;

        const isEstimated = this.estimatedDataPointIndices[seriesIndex][dataPointIndex];

        if (this.small) {
          return activeColor;
        }

        if (isEstimated) {
          return isBarSelected ? activeColorEstimated : inactiveColorEstimated;
        }

        return isBarSelected ? activeColor : inactiveColor;
      },
    ];
    this.stroke = {
      width: 1,
      colors: ['transparent'],
      curve: 'smooth',
    };
    this.plotOptions = this.createPlotOptions();
    this.dataLabels = {
      enabled: true,
      formatter: (_value, options) => {
        if (this.small) {
          return '';
        }
        const { seriesIndex, dataPointIndex } = options;
        const isEstimatedValue = this.estimatedDataPointIndices[seriesIndex][dataPointIndex];
        return isEstimatedValue ? '*' : '';
      },
      offsetY: -15,
      offsetX: 2,
      style: {
        fontSize: '12px',
        colors: ['var(--primary-default, #3486EF)'],
      },
    };
  }

  selectSeriesPeriod(seriesPeriod: SeriesPeriod): void {
    this.activeSeriesPeriod = seriesPeriod;
    this.updateXAxis();
    if (seriesPeriod !== SeriesPeriod.FULL_YEAR) {
      this.selectedDataPointIndex = null;
    }
    // normally not needed, but chart update via inputs does not always work
    void this.chart?.updateOptions({ xaxis: this.options.xaxis, colors: this.colors });
    void this.chart?.updateSeries(this.availableSeries[this.activeSeriesPeriod]);
    this.#cdr.markForCheck();
  }

  private updateSeries(consumptionIndex: ConsumptionSeriesIndex): void {
    const data =
      consumptionIndex === ConsumptionSeriesIndex.CONSUMPTION
        ? this.consumption
        : this.pastConsumption;

    this.estimatedDataPointIndices[consumptionIndex] = {};

    const availableSeries = this.availableSeries;

    const isDataEmpty = !Array.isArray(data) || data.length === 0;

    if (isDataEmpty) {
      const emptySeries: ApexAxisChartSeries[0] = { name: '', data: [] };
      availableSeries[SeriesPeriod.FULL_YEAR][consumptionIndex] = emptySeries;
      availableSeries[SeriesPeriod.FIRST_SEMESTER][consumptionIndex] = emptySeries;
      availableSeries[SeriesPeriod.SECOND_SEMESTER][consumptionIndex] = emptySeries;
      this.consumptionWithFittingDatapointIndex.set([]);
    } else {
      // prepare array with one entry for Jan - Dec and value null
      const fullYearData = Array.from({ length: 12 }).map(() => null);
      const consumptionWithFittingDatapointIndex = [];
      // fill prepared array with real values
      for (const entry of data) {
        const monthIndexOfConsumption = new Date(entry.period.start).getMonth();
        fullYearData[monthIndexOfConsumption] = parseFloat(entry.amount.toFixed(2));
        consumptionWithFittingDatapointIndex[monthIndexOfConsumption] = entry;

        if (entry.estimated) {
          this.estimatedDataPointIndices[consumptionIndex][monthIndexOfConsumption] = true;
        }
      }

      const name = `${this.getConsumptionYearFromData(data)}`;

      this.consumptionWithFittingDatapointIndex.set(consumptionWithFittingDatapointIndex);

      availableSeries[SeriesPeriod.FULL_YEAR][consumptionIndex] = {
        name,
        data: fullYearData, // Jan - Dec
      };

      availableSeries[SeriesPeriod.FIRST_SEMESTER][consumptionIndex] = {
        name,
        data: fullYearData.slice(0, 6), // Jan - Jun
      };
      availableSeries[SeriesPeriod.SECOND_SEMESTER][consumptionIndex] = {
        name,
        data: fullYearData.slice(6, 12), // Jul - Dec
      };
    }

    // return new object and arrays to support ChangeDetectionStrategy.OnPush
    this.availableSeries = {
      [SeriesPeriod.FULL_YEAR]: [...availableSeries[SeriesPeriod.FULL_YEAR]],
      [SeriesPeriod.FIRST_SEMESTER]: [...availableSeries[SeriesPeriod.FIRST_SEMESTER]],
      [SeriesPeriod.SECOND_SEMESTER]: [...availableSeries[SeriesPeriod.SECOND_SEMESTER]],
    };
  }

  public registerDataPointSelectionEventHandler() {
    this.options = {
      ...this.options,
      chart: {
        ...this.options.chart,
        events: {
          ...this.options.chart.events,
          dataPointSelection: (
            _event: MouseEvent,
            _context: unknown,
            { dataPointIndex }: ApexDataPointSelection
          ) => this.onDataPointSelection(dataPointIndex),
        },
      },
    };
  }

  public registerUpdateEventHandler() {
    this.options = {
      ...this.options,
      chart: {
        ...this.options.chart,
        events: {
          ...this.options.chart.events,
          updated: (_chart: unknown, _options: unknown) => {
            this.#lastChartRerender.next(new Date().getTime());
            this.#cdr.markForCheck();
          },
        },
      },
    };
  }

  public onDataPointSelection(dataPointIndex: number): void {
    this.selectedDataPointIndex = dataPointIndex;
    void this.chart?.updateOptions({ colors: this.colors });

    const indexOffset = this.activeSeriesPeriod === SeriesPeriod.SECOND_SEMESTER ? 6 : 0;
    const selectedEntry = this.consumptionWithFittingDatapointIndex()[dataPointIndex + indexOffset];
    if (!selectedEntry) {
      return;
    }
    this.consumptionSelected.emit(selectedEntry);
  }

  private reactOnBreakpointStateChange(state: BreakpointState) {
    if (this.small) {
      return;
    }
    this.isSemesterMode = state.matches;
    this.isSemesterMode
      ? this.selectSeriesPeriod(SeriesPeriod.FIRST_SEMESTER)
      : this.selectSeriesPeriod(SeriesPeriod.FULL_YEAR);
  }

  private createPlotOptions(): ApexPlotOptions {
    return {
      bar: {
        columnWidth: this.small ? '16px' : '20px',
        borderRadius: this.small ? 4 : 6,
        dataLabels: {
          position: 'top',
        },
      },
    };
  }

  private createXAxis(): ApexXAxis {
    const categories = this.availableCategories[this.activeSeriesPeriod];
    return {
      ...this.options?.xaxis,
      type: 'category',
      categories,
      labels: {
        ...this.options?.xaxis.labels,
        style: {
          cssClass: 'consumption-chart-axis-label--xaxis--year',
        },
        formatter: (value: string): string => {
          return this.small ? value.substring(0, 1) : value;
        },
      },
    };
  }

  private updateXAxis(): void {
    this.options = { ...this.options, xaxis: this.createXAxis() };
  }

  private getConsumptionYearFromData(data: IConsumptionItem[]): number {
    const [firstEntry] = data;
    return new Date(firstEntry.period.start).getFullYear();
  }

  private createMonthNames(): string[] {
    return Array.from({ length: 12 }).map((_value, index) => {
      const date = new Date(2023, index);
      return format(date, 'LLL', { locale: de }).toUpperCase();
    });
  }
}
